import { useMutation, useQuery } from 'urql'
import { useNavigate } from 'react-router-dom'
import { useConfirmation } from 'app/components/ConfirmationContext'
import type { FormVersions } from 'app/lib/forms/builder'
import { groupFormVersions, parseQuestions } from 'app/lib/forms/builder'
import type { FormTemplate as StarterTemplate } from 'app/lib/forms/formTemplates'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import {
  CREATE_FORM_TEMPLATE_MUTATION,
  DELETE_FORM_TEMPLATE_MUTATION,
  FORM_TEMPLATES_QUERY,
} from 'app/components/paperwork/graphql'
import type { PaperworkFormTemplatesQuery } from 'app/generated/gql/graphql'

export type FormTemplateRow = PaperworkFormTemplatesQuery['formTemplates']['hits'][number]
export type FormListEntry = FormVersions<FormTemplateRow>

/** Every version of every form. A workspace has tens of forms, not thousands. */
const ALL_VERSIONS = { page: 0, limit: 1000 }

export const FORM_EDITOR_PATH = `/settings/forms/library`

export type UseFormsListReturn = {
  forms: FormListEntry[]
  fetching: boolean
  creating: boolean
  createForm: (starter?: StarterTemplate) => Promise<void>
  openForm: (entry: FormListEntry) => void
  deleteForm: (entry: FormListEntry) => Promise<void>
  duplicateForm: (entry: FormListEntry) => Promise<void>
}

export function useFormsList(): UseFormsListReturn {
  const navigate = useNavigate()
  const [confirm] = useConfirmation()
  const [{ data, fetching }] = useQuery({
    query: FORM_TEMPLATES_QUERY,
    variables: { archived: false, pagination: ALL_VERSIONS },
    requestPolicy: `cache-and-network`,
  })
  const [{ fetching: creating }, executeCreate] = useMutation(CREATE_FORM_TEMPLATE_MUTATION)
  const [, executeDelete] = useMutation(DELETE_FORM_TEMPLATE_MUTATION)

  const forms = groupFormVersions(data?.formTemplates.hits ?? []).sort((a, b) =>
    a.latest.name.localeCompare(b.latest.name),
  )

  const createForm = async (starter?: StarterTemplate) => {
    const { data: created, error } = await executeCreate({
      input: {
        name: starter?.name ?? `Untitled form`,
        description: starter?.description,
        category: starter?.category ?? `custom`,
        questions: starter?.questions ?? [],
      },
    })
    if (error || !created) {
      notifyError(`Couldn't create the form`)
      logError(`useFormsList: create failed`, error?.message)
      return
    }
    navigate(`${FORM_EDITOR_PATH}/${created.createFormTemplate.id}`)
  }

  // A copy is a new form with its own history, so it starts from the newest version as a draft.
  const duplicateForm = async (entry: FormListEntry) => {
    const { data: created, error } = await executeCreate({
      input: {
        name: `Copy of ${entry.latest.name}`,
        description: entry.latest.description ?? undefined,
        category: entry.latest.category,
        questions: parseQuestions(entry.latest.questionsJson),
      },
    })
    if (error || !created) {
      notifyError(`Couldn't copy the form`)
      logError(`useFormsList: duplicate failed`, error?.message)
      return
    }
    navigate(`${FORM_EDITOR_PATH}/${created.createFormTemplate.id}`)
  }

  const deleteForm = async (entry: FormListEntry) => {
    const confirmed = await confirm({
      title: `Delete form`,
      description: `Delete "${entry.latest.name}"? This cannot be undone.`,
      variant: `danger`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    const { error } = await executeDelete({ id: entry.latest.id }, { additionalTypenames: [`FormTemplate`] })
    if (error) {
      notifyError(`Couldn't delete the form`)
      logError(`useFormsList: delete failed`, error.message)
      return
    }
    notifySuccess(`Form deleted`)
  }

  return {
    forms,
    fetching,
    creating,
    createForm,
    openForm: (entry) => navigate(`${FORM_EDITOR_PATH}/${entry.latest.id}`),
    deleteForm,
    duplicateForm,
  }
}
